import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, TrendingUp, Home, LogOut, Calendar, Download, Filter, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import RupiahIcon from '@/components/RupiahIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [owner, setOwner] = useState(null);
  const [overview, setOverview] = useState(null);
  const [properties, setProperties] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [complaints, setComplaints] = useState([]);
  
  // Filter states
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [complaintStatusFilter, setComplaintStatusFilter] = useState('all');
  const [complaintPropertyFilter, setComplaintPropertyFilter] = useState('all');
  const [complaintSortMode, setComplaintSortMode] = useState('latest');
  const [complaintUpdates, setComplaintUpdates] = useState([]);

  const applyRevenueFilters = (sourceTransactions, overrides = {}) => {
    let filtered = [...sourceTransactions];
    const propertyFilter = overrides.selectedProperty ?? selectedProperty;
    const monthFilter = overrides.selectedMonth ?? selectedMonth;
    const yearFilter = overrides.selectedYear ?? selectedYear;

    if (propertyFilter !== 'all') {
      filtered = filtered.filter((transaction) => transaction.property_name === propertyFilter);
    }

    if (monthFilter !== 'all' && yearFilter) {
      filtered = filtered.filter((transaction) => {
        const transactionDate = new Date(transaction.transaction_date);
        return (
          transactionDate.getMonth() === parseInt(monthFilter, 10) &&
          transactionDate.getFullYear() === parseInt(yearFilter, 10)
        );
      });
    } else if (yearFilter) {
      filtered = filtered.filter((transaction) => {
        const transactionDate = new Date(transaction.transaction_date);
        return transactionDate.getFullYear() === parseInt(yearFilter, 10);
      });
    }

    return filtered;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const ownerData = localStorage.getItem('owner');
    
    if (!token || !ownerData) {
      navigate('/login');
      return;
    }

    setOwner(JSON.parse(ownerData));
    if (location.state) {
      if (location.state.activeTab) {
        setActiveTab(location.state.activeTab);
      }
      if (location.state.selectedProperty) {
        setSelectedProperty(location.state.selectedProperty);
      }
      if (location.state.selectedMonth !== undefined) {
        setSelectedMonth(location.state.selectedMonth);
      }
      if (location.state.selectedYear) {
        setSelectedYear(location.state.selectedYear);
      }
    }
    fetchDashboardData(token);
  }, [location.state, navigate]);

  const fetchDashboardData = async (token) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [overviewRes, propertiesRes, transactionsRes, complaintsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard/overview`, config),
        axios.get(`${BACKEND_URL}/api/dashboard/properties`, config),
        axios.get(`${BACKEND_URL}/api/dashboard/revenue`, config),
        axios.get(`${BACKEND_URL}/api/owner/complaints`, config).catch(() => ({ data: [] }))
      ]);

      setOverview(overviewRes.data);
      setProperties(propertiesRes.data);
      setTransactions(transactionsRes.data);
      setFilteredTransactions(
        applyRevenueFilters(transactionsRes.data, {
          selectedProperty: location.state?.selectedProperty,
          selectedMonth: location.state?.selectedMonth,
          selectedYear: location.state?.selectedYear,
        })
      );
      const complaintsData = complaintsRes.data || [];
      setComplaints(complaintsData);

      // Track complaint status changes for lightweight notifications
      try {
        const ownerRaw = localStorage.getItem('owner');
        const ownerEmail = ownerRaw ? JSON.parse(ownerRaw).email : 'owner';
        const snapshotKey = `owner_complaints_snapshot_${ownerEmail}`;
        const prevSnapshot = JSON.parse(localStorage.getItem(snapshotKey) || '{}');
        const nextSnapshot = {};
        const updates = [];
        complaintsData.forEach(c => {
          const id = c.complaint_id;
          if (!id) return;
          nextSnapshot[id] = c.status;
          if (prevSnapshot[id] && prevSnapshot[id] !== c.status) {
            updates.push({ complaint_id: id, from: prevSnapshot[id], to: c.status });
          }
        });
        setComplaintUpdates(updates);
        localStorage.setItem(snapshotKey, JSON.stringify(nextSnapshot));
      } catch {
        setComplaintUpdates([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('owner');
    navigate('/login');
  };


  const handleFilterApply = () => {
    setFilteredTransactions(applyRevenueFilters(transactions));
  };

  const getCategoryLabel = (category) => {
    const map = {
      rent_payment: 'Sewa',
      laundry: 'Laundry',
      cleaning: 'Kebersihan',
      cleaning_fee: 'Kebersihan',
      deposit: 'Deposit',
      deposit_returned: 'Deposit Kembali',
      deposit_forfeited: 'Deposit Hangus',
      maintenance: 'Perawatan',
      maintenance_fee: 'Perawatan',
      repair: 'Perbaikan',
      utilities: 'Utilitas',
      operational: 'Operasional',
      other_income: 'Lain-lain',
      other_expense: 'Pengeluaran Lain',
      parkir: 'Parkir',
    };
    return map[category] || category || 'Lainnya';
  };

  const getRevenueSummary = (list) => {
    const incomeTypes = ['rent', 'income', 'rent_payment'];
    const expenseTypes = ['maintenance_fee', 'maintenance', 'expense', 'repair', 'utilities', 'operational', 'other_expense'];
    let income = 0;
    let expense = 0;
    list.forEach(t => {
      if (t.transaction_type) {
        if (t.transaction_type === 'expense') expense += t.amount || 0;
        else income += t.amount || 0;
        return;
      }
      const type = (t.type || '').toLowerCase();
      if (expenseTypes.includes(type)) expense += t.amount || 0;
      else if (incomeTypes.includes(type)) income += t.amount || 0;
      else income += t.amount || 0;
    });
    return { income, expense, net: income - expense };
  };

  const handleDownloadReport = async () => {
    try {
      const token = localStorage.getItem('token');
      
      let startDate = '';
      let endDate = '';
      
      if (selectedYear) {
        if (selectedMonth !== 'all') {
          // Specific month
          const month = parseInt(selectedMonth);
          startDate = `${selectedYear}-${String(month + 1).padStart(2, '0')}-01`;
          const lastDay = new Date(parseInt(selectedYear), month + 1, 0).getDate();
          endDate = `${selectedYear}-${String(month + 1).padStart(2, '0')}-${lastDay}`;
        } else {
          // Whole year
          startDate = `${selectedYear}-01-01`;
          endDate = `${selectedYear}-12-31`;
        }
      }

      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedProperty !== 'all') params.append('property_name', selectedProperty);

      const response = await axios.get(
        `${BACKEND_URL}/api/dashboard/revenue/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Laporan_Pendapatan_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Gagal mengunduh laporan. Silakan coba lagi.');
    }
  };

  const handleDownloadPdf = () => {
    const title = 'Laporan Transaksi';
    const ownerName = owner?.name || 'Owner';
    const periodText = selectedMonth !== 'all' && selectedYear
      ? `${['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'][parseInt(selectedMonth)]} ${selectedYear}`
      : selectedYear ? `Tahun ${selectedYear}` : 'Semua Periode';
    const propertyText = selectedProperty !== 'all' ? selectedProperty : 'Semua Properti';

    const summary = getRevenueSummary(filteredTransactions);

    const rows = filteredTransactions.map(t => `
      <tr>
        <td>${formatDate(t.transaction_date)}</td>
        <td>${t.property_name || '-'}</td>
        <td>${t.description || '-'}</td>
        <td>${t.type || '-'}</td>
        <td style="text-align:right;">${formatCurrency(t.amount || 0)}</td>
      </tr>
    `).join('');

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #0E1111; }
            h1 { margin: 0 0 8px; font-size: 20px; }
            .meta { margin-bottom: 16px; font-size: 12px; color: #64748B; }
            .summary { margin: 16px 0; display: flex; gap: 12px; font-size: 12px; }
            .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 12px; }
            th, td { border-bottom: 1px solid #e5e7eb; padding: 8px; text-align: left; }
            th { background: #f9fafb; }
            .footer { margin-top: 16px; font-size: 11px; color: #64748B; }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <div class="meta">Owner: ${ownerName} · Properti: ${propertyText} · Periode: ${periodText}</div>
          <div class="summary">
            <div class="card">Total Pemasukan: <strong>${formatCurrency(summary.income)}</strong></div>
            <div class="card">Total Pengeluaran: <strong>${formatCurrency(summary.expense)}</strong></div>
            <div class="card">Net: <strong>${formatCurrency(summary.net)}</strong></div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Properti</th>
                <th>Deskripsi</th>
                <th>Tipe</th>
                <th style="text-align:right;">Jumlah</th>
              </tr>
            </thead>
            <tbody>
              ${rows || '<tr><td colspan="5">Tidak ada data</td></tr>'}
            </tbody>
          </table>
          <div class="footer">Exported ${new Date().toLocaleString('id-ID')}</div>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i.toString());
    }
    return years;
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getComplaintCompletionDate = (complaint) => {
    if (complaint.status !== 'closed') return null;
    return complaint.completed_at || complaint.updated_at || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-isk-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-isk-muted">Memuat dashboard...</p>
        </div>
      </div>
    );
  }

  const activeComplaints = complaints.filter(c => c.status !== 'closed');
  const complaintCounts = {
    new: complaints.filter(c => c.status === 'new').length,
    in_progress: complaints.filter(c => c.status === 'in_progress').length,
    closed: complaints.filter(c => c.status === 'closed').length
  };
  const filteredComplaints = complaints.filter(c => {
    if (complaintStatusFilter !== 'all' && c.status !== complaintStatusFilter) return false;
    if (complaintPropertyFilter !== 'all' && c.property_name !== complaintPropertyFilter) return false;
    return true;
  });
  const roomComplaintCounts = filteredComplaints.reduce((acc, complaint) => {
    const key = `${complaint.property_name || '-'}::${complaint.room_number || '-'}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const propertyComplaintCounts = filteredComplaints.reduce((acc, complaint) => {
    const key = complaint.property_name || '-';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const visibleComplaints = [...filteredComplaints].sort((a, b) => {
    const dateDiff = new Date(b.created_at) - new Date(a.created_at);

    if (complaintSortMode === 'room_most') {
      const roomA = `${a.property_name || '-'}::${a.room_number || '-'}`;
      const roomB = `${b.property_name || '-'}::${b.room_number || '-'}`;
      const countDiff = (roomComplaintCounts[roomB] || 0) - (roomComplaintCounts[roomA] || 0);
      if (countDiff !== 0) return countDiff;
      return dateDiff;
    }

    if (complaintSortMode === 'property_most') {
      const countDiff = (propertyComplaintCounts[b.property_name || '-'] || 0) - (propertyComplaintCounts[a.property_name || '-'] || 0);
      if (countDiff !== 0) return countDiff;
      return dateDiff;
    }

    return dateDiff;
  });

  return (
    <div className="min-h-screen bg-isk-bg">
      {/* Header - Fixed at top */}
      <div className="glass-nav border-b border-white/20 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <h1 className="text-lg sm:text-2xl font-bold text-isk-teal">ISK</h1>
            <span className="text-isk-muted hidden sm:inline">|</span>
            <span className="text-isk-text font-medium text-sm sm:text-base hidden sm:inline">Dashboard</span>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-isk-text">{owner?.name}</p>
              <p className="text-xs text-isk-muted">{owner?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-2 border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
        
        {/* Tabs - Fixed below header */}
        <div className="border-t border-white/10 bg-isk-bg/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-3 sm:px-6">
            <div className="flex gap-1.5 sm:gap-2 py-2 overflow-x-auto scrollbar-hide">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'properties', label: 'Properti' },
                { key: 'revenue', label: 'Laporan' },
              ].map(tab => (
                <button key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full font-semibold transition-all whitespace-nowrap text-xs sm:text-sm ${
                    activeTab === tab.key ? 'bg-isk-teal text-white shadow-lg' : 'bg-white/60 text-isk-text hover:bg-white/80'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >{tab.label}</button>
              ))}
              <button
                onClick={() => setActiveTab('complaints')}
                className={`relative px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full font-semibold transition-all whitespace-nowrap text-xs sm:text-sm flex items-center gap-1 ${
                  activeTab === 'complaints' ? 'bg-isk-teal text-white shadow-lg' : 'bg-white/60 text-isk-text hover:bg-white/80'
                }`}
                data-testid="tab-complaints"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                Komplain
                {complaints.filter(c => c.status !== 'closed').length > 0 && (
                  <span className="ml-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                    {complaints.filter(c => c.status !== 'closed').length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - with padding for fixed header and tabs */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6 sm:space-y-8">
            {complaintUpdates.length > 0 && (
              <div className="glass-card rounded-xl p-4 border border-orange-200 bg-orange-50/60">
                <p className="text-sm text-orange-700 font-semibold">
                  Ada {complaintUpdates.length} komplain yang berubah status sejak terakhir Anda membuka dashboard.
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  Cek tab Komplain untuk detail update terbaru.
                </p>
              </div>
            )}
            {/* Metrics Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
              {[
                { icon: Building2, color: 'bg-isk-teal/10', iconColor: 'text-isk-teal', value: overview.total_properties, label: 'Properti', testid: 'metric-properties' },
                { icon: Home, color: 'bg-isk-gold/10', iconColor: 'text-isk-gold', value: `${overview.average_occupancy}%`, label: `${overview.occupied_rooms}/${overview.total_rooms} kamar`, testid: 'metric-occupancy' },
                { icon: RupiahIcon, color: 'bg-green-500/10', iconColor: 'text-green-600', value: formatCurrency(overview.monthly_revenue), label: 'Bulan Ini', testid: 'metric-monthly-revenue', small: true },
                { icon: TrendingUp, color: 'bg-isk-teal/10', iconColor: 'text-isk-teal', value: formatCurrency(overview.total_revenue), label: 'Total', testid: 'metric-total-revenue', small: true },
                { icon: AlertTriangle, color: 'bg-orange-500/10', iconColor: 'text-orange-500', value: activeComplaints.length, label: 'Komplain Aktif', testid: 'metric-complaints' },
              ].map(({ icon: Icon, color, iconColor, value, label, testid, small }) => (
                <div key={testid} className="glass-card rounded-xl p-3 sm:p-5 hover:-translate-y-0.5 transition-all" data-testid={testid}>
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${color} flex items-center justify-center mb-2 sm:mb-3`}>
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
                  </div>
                  <p className={`font-bold text-isk-text mb-0.5 ${small ? 'text-sm sm:text-lg' : 'text-xl sm:text-2xl'}`}>{value}</p>
                  <p className="text-[11px] sm:text-xs text-isk-muted leading-tight">{label}</p>
                </div>
              ))}
            </div>

            {/* Properties Summary */}
            <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <h3 className="text-base sm:text-xl font-bold text-isk-text mb-3 sm:mb-4">Properti Anda</h3>
              <div className="space-y-2">
                {properties.slice(0, 3).map((property, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-white/30 hover:bg-white/50 transition-colors gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-isk-text text-sm truncate">{property.name}</h4>
                      <p className="text-xs text-isk-muted truncate">{property.location}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-isk-teal">{property.occupancy_rate}%</p>
                      <p className="text-xs text-isk-muted">{property.occupied_rooms}/{property.total_rooms}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div className="space-y-3 sm:space-y-4">
            {properties.map((property, index) => (
              <button
                key={index}
                type="button"
                onClick={() => navigate(`/dashboard/properties/${encodeURIComponent(property.name)}`, {
                  state: { activeTab: 'properties' }
                })}
                className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6 w-full text-left hover:-translate-y-0.5 transition-all"
                data-testid={`property-${index}`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base sm:text-xl font-bold text-isk-text truncate">{property.name}</h3>
                    <p className="text-xs sm:text-sm text-isk-muted truncate mt-0.5">{property.location}</p>
                  </div>
                  <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    property.status === 'active' ? 'bg-isk-teal/10 text-isk-teal' : 'bg-gray-100 text-isk-muted'
                  }`}>
                    {property.status === 'active' ? 'Aktif' : 'Tidak Aktif'}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="p-2.5 sm:p-3 rounded-xl bg-white/50">
                    <p className="text-[11px] sm:text-xs text-isk-muted mb-0.5">Total Kamar</p>
                    <p className="text-lg sm:text-2xl font-bold text-isk-text">{property.total_rooms}</p>
                  </div>
                  <div className="p-2.5 sm:p-3 rounded-xl bg-white/50">
                    <p className="text-[11px] sm:text-xs text-isk-muted mb-0.5">Terisi</p>
                    <p className="text-lg sm:text-2xl font-bold text-isk-teal">{property.occupied_rooms}</p>
                  </div>
                  <div className="p-2.5 sm:p-3 rounded-xl bg-white/50">
                    <p className="text-[11px] sm:text-xs text-isk-muted mb-0.5">Okupansi</p>
                    <p className="text-lg sm:text-2xl font-bold text-isk-gold">{property.occupancy_rate}%</p>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-isk-teal to-isk-gold transition-all"
                      style={{ width: `${property.occupancy_rate}%` }} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {/* Filter Section */}
            <div className="glass-card rounded-2xl p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 gap-2">
                <h3 className="text-base sm:text-xl font-bold text-isk-text flex items-center">
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5" />
                  Filter
                </h3>
                <div className="flex items-center gap-1.5">
                  <Button
                    onClick={handleDownloadReport}
                    className="bg-isk-gold hover:bg-isk-gold-hover text-white rounded-full px-3 sm:px-5 py-2 text-xs sm:text-sm font-semibold shadow-lg"
                    data-testid="download-report-button"
                  >
                    <Download className="w-3.5 h-3.5 sm:mr-1.5" />
                    <span className="hidden sm:inline">Excel</span>
                  </Button>
                  <Button
                    onClick={handleDownloadPdf}
                    variant="outline"
                    className="border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full px-3 sm:px-5 py-2 text-xs sm:text-sm font-semibold"
                    data-testid="download-pdf-button"
                  >
                    PDF
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                {/* Year Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-isk-text">Tahun</label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="select-year">
                      <SelectValue placeholder="Pilih Tahun" />
                    </SelectTrigger>
                    <SelectContent>
                      {generateYearOptions().map(year => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Month Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-isk-text">Bulan</label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="select-month">
                      <SelectValue placeholder="Semua Bulan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Bulan</SelectItem>
                      <SelectItem value="0">Januari</SelectItem>
                      <SelectItem value="1">Februari</SelectItem>
                      <SelectItem value="2">Maret</SelectItem>
                      <SelectItem value="3">April</SelectItem>
                      <SelectItem value="4">Mei</SelectItem>
                      <SelectItem value="5">Juni</SelectItem>
                      <SelectItem value="6">Juli</SelectItem>
                      <SelectItem value="7">Agustus</SelectItem>
                      <SelectItem value="8">September</SelectItem>
                      <SelectItem value="9">Oktober</SelectItem>
                      <SelectItem value="10">November</SelectItem>
                      <SelectItem value="11">Desember</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Property Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-isk-text">Properti</label>
                  <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                    <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl" data-testid="select-property">
                      <SelectValue placeholder="Semua Properti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Properti</SelectItem>
                      {properties.map((prop, idx) => (
                        <SelectItem key={idx} value={prop.name}>{prop.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Apply Button */}
                <div className="flex items-end">
                  <Button
                    onClick={handleFilterApply}
                    className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-xl px-6 py-6 font-semibold"
                    data-testid="apply-filter-button"
                  >
                    Tampilkan Laporan
                  </Button>
                </div>
              </div>

              {/* Filter Info */}
              <div className="mt-4 p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20">
                <p className="text-sm text-isk-muted">
                  Menampilkan <span className="font-bold text-isk-teal">{filteredTransactions.length}</span> transaksi
                  {selectedProperty !== 'all' && ` dari ${selectedProperty}`}
                  {selectedMonth !== 'all' && selectedYear && ` untuk ${['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'][parseInt(selectedMonth)]} ${selectedYear}`}
                  {selectedMonth === 'all' && selectedYear && ` untuk tahun ${selectedYear}`}
                </p>
              </div>
            </div>

            {/* Table Section */}
            <div className="glass-card rounded-2xl p-4 sm:p-6">
              {/* Cashflow Summary */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
                {(() => {
                  const summary = getRevenueSummary(filteredTransactions);
                  return (
                    <>
                      <div className="p-2.5 sm:p-4 rounded-xl bg-white/50 border border-white/20">
                        <p className="text-[10px] sm:text-xs text-isk-muted">Pemasukan</p>
                        <p className="text-sm sm:text-xl font-bold text-green-600">{formatCurrency(summary.income)}</p>
                      </div>
                      <div className="p-2.5 sm:p-4 rounded-xl bg-white/50 border border-white/20">
                        <p className="text-[10px] sm:text-xs text-isk-muted">Pengeluaran</p>
                        <p className="text-sm sm:text-xl font-bold text-red-600">{formatCurrency(summary.expense)}</p>
                      </div>
                      <div className="p-2.5 sm:p-4 rounded-xl bg-white/50 border border-white/20">
                        <p className="text-[10px] sm:text-xs text-isk-muted">Net</p>
                        <p className="text-sm sm:text-xl font-bold text-isk-teal">{formatCurrency(summary.net)}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
              <h3 className="text-base sm:text-xl font-bold text-isk-text mb-3 sm:mb-5">Riwayat Transaksi</h3>

              {/* Mobile card list */}
              <div className="sm:hidden space-y-2">
                {filteredTransactions.length === 0 ? (
                  <p className="text-center text-isk-muted py-8 text-sm">Tidak ada transaksi</p>
                ) : filteredTransactions.map((t, i) => (
                  <div key={i} className="flex items-start justify-between p-3 rounded-xl bg-white/40 gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${t.transaction_type === 'expense' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {getCategoryLabel(t.category)}
                        </span>
                        <span className="text-[11px] text-isk-muted">{formatDate(t.transaction_date)}</span>
                      </div>
                      <p className="text-xs font-medium text-isk-text truncate">{t.description || t.property_name}</p>
                    </div>
                    <p className={`text-sm font-bold shrink-0 ${t.transaction_type === 'expense' ? 'text-red-600' : 'text-isk-teal'}`}>
                      {t.transaction_type === 'expense' ? '-' : ''}{formatCurrency(t.amount)}
                    </p>
                  </div>
                ))}
                {filteredTransactions.length > 0 && (() => {
                  const s = getRevenueSummary(filteredTransactions);
                  return <div className="flex justify-between p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20 mt-2">
                    <span className="text-sm font-bold text-isk-text">Net Profit</span>
                    <span className={`text-sm font-bold ${s.net >= 0 ? 'text-isk-teal' : 'text-red-600'}`}>{formatCurrency(s.net)}</span>
                  </div>;
                })()}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full" data-testid="revenue-table">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-isk-text">Tanggal</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-isk-text">Properti</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-isk-text">Deskripsi</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-isk-text">Tipe</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-isk-text">Jumlah</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.length > 0 ? (
                      filteredTransactions.map((transaction, index) => (
                        <tr key={index} className="border-b border-white/10 hover:bg-white/30 transition-colors">
                          <td className="py-2.5 px-3 text-xs text-isk-muted">{formatDate(transaction.transaction_date)}</td>
                          <td className="py-2.5 px-3 text-xs text-isk-text font-medium">{transaction.property_name}</td>
                          <td className="py-2.5 px-3 text-xs text-isk-muted">{transaction.description}</td>
                          <td className="py-2.5 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${transaction.transaction_type === 'expense' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {getCategoryLabel(transaction.category)}
                            </span>
                          </td>
                          <td className={`py-2.5 px-3 text-sm font-bold text-right ${transaction.transaction_type === 'expense' ? 'text-red-600' : 'text-isk-teal'}`}>
                            {transaction.transaction_type === 'expense' ? '-' : ''}{formatCurrency(transaction.amount)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="5" className="py-8 text-center text-isk-muted text-sm">Tidak ada transaksi</td></tr>
                    )}
                  </tbody>
                  {filteredTransactions.length > 0 && (() => {
                    const s = getRevenueSummary(filteredTransactions);
                    return <tfoot><tr className="border-t-2 border-isk-teal/20">
                      <td colSpan="4" className="py-3 px-3 text-right font-bold text-isk-text text-sm">NET PROFIT:</td>
                      <td className={`py-3 px-3 text-right font-bold text-base ${s.net >= 0 ? 'text-isk-teal' : 'text-red-600'}`}>{formatCurrency(s.net)}</td>
                    </tr></tfoot>;
                  })()}
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Complaints Tab */}
        {activeTab === 'complaints' && (
          <div className="space-y-6">
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl font-bold text-isk-text mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-2 text-orange-500" />
                Komplain Properti Anda
              </h3>

              {complaintUpdates.length > 0 && (
                <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 text-sm">
                  {complaintUpdates.length} komplain berubah status sejak terakhir Anda membuka dashboard.
                </div>
              )}

              <div className="glass-card rounded-xl p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-isk-text">Status</label>
                    <Select value={complaintStatusFilter} onValueChange={setComplaintStatusFilter}>
                      <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                        <SelectValue placeholder="Semua Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Status</SelectItem>
                        <SelectItem value="new">Baru</SelectItem>
                        <SelectItem value="in_progress">Diproses</SelectItem>
                        <SelectItem value="closed">Selesai</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-isk-text">Properti</label>
                    <Select value={complaintPropertyFilter} onValueChange={setComplaintPropertyFilter}>
                      <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                        <SelectValue placeholder="Semua Properti" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Properti</SelectItem>
                        {properties.map((prop, idx) => (
                          <SelectItem key={idx} value={prop.name}>{prop.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-isk-text">Urutkan</label>
                    <Select value={complaintSortMode} onValueChange={setComplaintSortMode}>
                      <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                        <SelectValue placeholder="Urutan Komplain" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="latest">Terbaru</SelectItem>
                        <SelectItem value="room_most">Kamar Terbanyak</SelectItem>
                        <SelectItem value="property_most">Properti Terbanyak</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {visibleComplaints.length > 0 ? (
                <div className="space-y-4">
                  {visibleComplaints.map((complaint, idx) => (
                    <div key={idx} className="p-4 rounded-xl bg-white/50 border border-white/20 hover:bg-white/70 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                        <div>
                          <span className="font-mono text-sm font-semibold text-isk-teal">{complaint.complaint_id}</span>
                          <p className="font-medium text-isk-text mt-1">{complaint.property_name} - Kamar {complaint.room_number}</p>
                          {complaintSortMode === 'room_most' && (
                            <p className="text-xs text-isk-muted mt-1">
                              Total komplain kamar ini: <span className="font-semibold text-isk-teal">{roomComplaintCounts[`${complaint.property_name || '-'}::${complaint.room_number || '-'}`] || 0}</span>
                            </p>
                          )}
                          {complaintSortMode === 'property_most' && (
                            <p className="text-xs text-isk-muted mt-1">
                              Total komplain properti ini: <span className="font-semibold text-isk-teal">{propertyComplaintCounts[complaint.property_name || '-'] || 0}</span>
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            complaint.status === 'new' ? 'bg-yellow-100 text-yellow-700' :
                            complaint.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {complaint.status === 'new' ? 'Baru' : 
                             complaint.status === 'in_progress' ? 'Diproses' : 'Selesai'}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            complaint.priority === 'emergency' ? 'bg-red-100 text-red-700' :
                            complaint.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                            complaint.priority === 'medium' ? 'bg-blue-100 text-blue-700' :
                            'bg-isk-bg text-isk-muted'
                          }`}>
                            {complaint.priority === 'emergency' ? 'Darurat' :
                             complaint.priority === 'high' ? 'Tinggi' :
                             complaint.priority === 'medium' ? 'Sedang' : 'Rendah'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-3 mb-3">
                        <div>
                          <p className="text-xs text-isk-muted">Tenant</p>
                          <p className="text-sm font-medium text-isk-text">{complaint.tenant_name || complaint.tenant_phone}</p>
                        </div>
                        <div>
                          <p className="text-xs text-isk-muted">Kategori</p>
                          <p className="text-sm font-medium text-isk-text">{complaint.category}</p>
                        </div>
                      </div>
                      
                      <p className="text-sm text-isk-muted mb-2">{complaint.description}</p>
                      
                      {complaint.photo_url && (
                        <img 
                          src={`${BACKEND_URL}${complaint.photo_url}`} 
                          alt="Foto komplain" 
                          className="w-full sm:w-48 h-32 object-cover rounded-lg mb-2"
                        />
                      )}
                      
                      <div className="flex flex-wrap gap-4 text-xs text-isk-muted mt-3 pt-3 border-t border-white/20">
                        <span>Dilaporkan: {formatDate(complaint.created_at)}</span>
                        {complaint.estimated_date && (
                          <span className="text-isk-teal">Estimasi: {formatDate(complaint.estimated_date)}</span>
                        )}
                        {getComplaintCompletionDate(complaint) && (
                          <span className="text-green-700 font-medium">
                            Tanggal Penyelesaian: {formatDate(getComplaintCompletionDate(complaint))}
                          </span>
                        )}
                      </div>
                      
                      {/* Owner Cost Alert */}
                      {complaint.owner_cost && complaint.owner_cost > 0 && (
                        <div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                          <p className="text-sm font-semibold text-orange-700">
                            Butuh Biaya Owner: {formatCurrency(complaint.owner_cost)}
                          </p>
                          {complaint.owner_cost_description && (
                            <p className="text-xs text-orange-600 mt-1">{complaint.owner_cost_description}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-isk-muted">
                  <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Tidak ada komplain</p>
                  <p className="text-sm mt-1">
                    {complaints.length === 0 ? 'Semua properti Anda dalam kondisi baik' : 'Filter yang dipilih tidak memiliki data'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
